import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, doc, onSnapshot, updateDoc, query, orderBy, limit, getDocs } from "firebase/firestore";
import { firebase } from '../../../../services/firebase';
import { logEvent, logError } from '../../../../utils/analytics';

// Module-level cache for session persistence
let chatsCache = null;
let unreadMessagesCache = null;
let lastUserId = null;

export const useChats = (user) => {
  const [state, setState] = useState({
    guestChats: null,
    unreadMessages: {},
    isLoading: true,
    error: null
  });
  const unreadMessagesRef = useRef({});

  const logChatError = useCallback((error, context = {}) => {
    console.error('Chat Error:', error, context);
    logError('useChats', error, context);
  }, []);

  // On mount, use cache if available and user is the same
  useEffect(() => {
    if (!user?.uid) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'No user ID available'
      }));
      return;
    }

    if (chatsCache && unreadMessagesCache && lastUserId === user.uid) {
      setState({
        guestChats: chatsCache,
        unreadMessages: unreadMessagesCache,
        isLoading: false,
        error: null
      });
    }
  }, [user]);

  const markChatAsRead = useCallback(async (chatId) => {
    try {
      const messagesRef = collection(firebase, `users/${user.uid}/chats/${chatId}/messages`);
      const unreadQuery = query(messagesRef, orderBy("timestamp", "desc"));
      const unreadSnap = await getDocs(unreadQuery);

      const updatePromises = unreadSnap.docs
        .filter(doc => doc.data().status === 'unread')
        .map(doc => updateDoc(doc.ref, { status: 'read' }));

      await Promise.all(updatePromises);
      logEvent('markChatAsRead', { chatId });
    } catch (error) {
      logError('markMessagesAsRead', error, { chatId });
    }
  }, [user]);

  const turnOffBuzz = useCallback(async (chatId) => {
    try {
      const messagesRef = collection(firebase, `users/${user.uid}/chats/${chatId}/messages`);
      const q = query(messagesRef, orderBy("timestamp", "desc"), limit(1));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const lastMessage = querySnapshot.docs[0];
        await updateDoc(lastMessage.ref, {
          buzzed: false,
          buzzTimestamp: null
        });
        logEvent('turnOffBuzz', { chatId });
      }
    } catch (error) {
      logError('turnOffBuzz', error, { chatId });
    }
  }, [user]);

  useEffect(() => {
    if (!user?.uid) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'No user UID available'
      }));
      return;
    }

    let unsubscribeChats = () => { };
    let messageListeners = {};
    let isMounted = true;

    const userChatsRef = collection(firebase, "users", user.uid, "chats");

    unsubscribeChats = onSnapshot(userChatsRef,
      (snapshot) => {
        if (!isMounted) return;


        const chats = snapshot.docs
          .map(doc => ({
            id: doc.id,
            closed: doc.data().closed || false,
            tag: doc.data().tag || null,
            shopperName: doc.data().shopperName || null,
            lastMessageTimestamp: doc.data().lastMessageTimestamp || 0
          }));

        // Set up message listeners for each chat
        chats.forEach(chat => {
          if (!messageListeners[chat.id]) {
            const messagesRef = collection(firebase, `users/${user.uid}/chats/${chat.id}/messages`);
            const q = query(messagesRef, orderBy("timestamp", "desc"), limit(1));

            messageListeners[chat.id] = onSnapshot(q, (messageSnap) => {
              const messages = messageSnap.docs.map(doc => doc.data());
              const hasUnread = messages.some(msg => msg.status === 'unread');
              const latestMessage = messages[0] || {};

              const chatRef = doc(firebase, `users/${user.uid}/chats/${chat.id}`);
              updateDoc(chatRef, {
                lastMessageTimestamp: latestMessage.timestamp || 0
              });

              // Update ref first
              unreadMessagesRef.current = {
                ...unreadMessagesRef.current,
                [chat.id]: {
                  hasUnread,
                  buzzed: latestMessage.buzzed || false,
                  buzzTimestamp: latestMessage.buzzTimestamp
                }
              };

              // Then update state
              setState(prev => ({
                ...prev,
                unreadMessages: unreadMessagesRef.current
              }));
            });
          }
        });

        // Clean up message listeners for removed chats
        Object.keys(messageListeners).forEach(chatId => {
          if (!chats.find(chat => chat.id === chatId)) {
            messageListeners[chatId]();
            delete messageListeners[chatId];
            delete unreadMessagesRef.current[chatId];
          }
        });

        // Sort chats based on priority and timestamp
        const sortedChats = chats.sort((a, b) => {
          const aStatus = unreadMessagesRef.current[a.id] || {};
          const bStatus = unreadMessagesRef.current[b.id] || {};

          // Move "Block" chats to the bottom
          if (a.tag === 'Block' && b.tag !== 'Block') return 1;
          if (a.tag !== 'Block' && b.tag === 'Block') return -1;

          if (a.tag === 'Spam' && b.tag !== 'Spam') return 1;
          if (a.tag !== 'Spam' && b.tag === 'Spam') return -1;

          if ((a.tag === 'Interested' || a.tag === 'Sold') &&
            (b.tag !== 'Interested' && b.tag !== 'Sold')) return -1;
          if ((a.tag !== 'Interested' && a.tag !== 'Sold') &&
            (b.tag === 'Interested' || b.tag === 'Sold')) return 1;

          const aIsBuzzed = aStatus.buzzed && aStatus.buzzTimestamp &&
            (new Date().getTime() - aStatus.buzzTimestamp.toDate().getTime()) < 5000;
          const bIsBuzzed = bStatus.buzzed && bStatus.buzzTimestamp &&
            (new Date().getTime() - bStatus.buzzTimestamp.toDate().getTime()) < 5000;

          if (aIsBuzzed && !bIsBuzzed) return -1;
          if (!aIsBuzzed && bIsBuzzed) return 1;

          return (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0);
        });

        // Unread messages logic (simplified for brevity)
        // ... you may want to update unreadMessagesRef here as in your original code
        const unreadMessages = unreadMessagesRef.current;

        setState(prev => ({
          ...prev,
          guestChats: sortedChats,
          unreadMessages,
          isLoading: false
        }));
        // Update cache
        chatsCache = sortedChats;
        unreadMessagesCache = unreadMessages;
        lastUserId = user.uid;
      });

    return () => {
      isMounted = false;
      unsubscribeChats();
      Object.values(messageListeners).forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing from chat listener:', error);
        }
      });
    };
  }, [user]);

  // Manual refresh: clears cache and triggers refetch
  const refreshChats = () => {
    chatsCache = null;
    unreadMessagesCache = null;
    lastUserId = null;
    setState(prev => ({ ...prev, guestChats: null, isLoading: true }));
  };

  return {
    guestChats: state.guestChats,
    unreadMessages: state.unreadMessages,
    markChatAsRead,
    turnOffBuzz,
    isLoading: state.isLoading,
    error: state.error,
    refreshChats
  };
};